import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Tooltip,
  Input,
  Dropdown,
  Menu,
  Divider,
  Message,
} from '@arco-design/web-react';
import {
  IconNotification,
  IconSunFill,
  IconMoonFill,
  IconPoweroff,
} from '@arco-design/web-react/icon';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { GlobalState } from '@/store';
import { GlobalContext } from '@/context';
import useLocale from '@/utils/useLocale';
import Logo from '@/assets/logo.svg';
import MessageBox from '@/components/MessageBox';
import IconButton from './IconButton';
import UserAvatar from '@/components/UserAvatar';
import styles from './style/index.module.less';
import useStorage from '@/utils/useStorage';
import { generatePermission } from '@/routes';
import { signOut } from '@/utils/supabaseAuth';
import GlobalSearchModal from './GlobalSearchModal';
import TaskDetailDrawer from '@/pages/tasks/components/task-detail-drawer';
import { useBatchProjectPermissions } from '@/hooks/useProjectPermission';
import type { Task } from '@/types/task';
import type { Project } from '@/types/project';

const PERMISSION_CODE_MAP = {
  'task:change_status_all': 'canChangeStatusAll',
  'task:change_status_own': 'canChangeStatusOwn',
  'task:edit_info_all': 'canEditInfoAll',
  'task:edit_info_own': 'canEditInfoOwn',
  'task:edit_date': 'canEditDate',
  'task:assign': 'canAssign',
  'task:delete': 'canDelete',
  'hours:plan_add_all': 'canAddPlannedHoursAll',
  'hours:plan_add_own': 'canAddPlannedHoursOwn',
  'hours:plan_edit_all': 'canEditPlannedHoursAll',
  'hours:plan_edit_own': 'canEditPlannedHoursOwn',
  'hours:actual_add_all': 'canAddActualHoursAll',
  'hours:actual_add_own': 'canAddActualHoursOwn',
  'hours:actual_edit_all': 'canEditActualHoursAll',
  'hours:actual_edit_own': 'canEditActualHoursOwn',
} as const;

const PERMISSION_CODES = Object.keys(PERMISSION_CODE_MAP);

type ProjectPermissions = {
  canChangeStatusAll: boolean;
  canChangeStatusOwn: boolean;
  canEditInfoAll: boolean;
  canEditInfoOwn: boolean;
  canEditDate: boolean;
  canAssign: boolean;
  canDelete: boolean;
  canAddPlannedHoursAll: boolean;
  canAddPlannedHoursOwn: boolean;
  canEditPlannedHoursAll: boolean;
  canEditPlannedHoursOwn: boolean;
  canAddActualHoursAll: boolean;
  canAddActualHoursOwn: boolean;
  canEditActualHoursAll: boolean;
  canEditActualHoursOwn: boolean;
};

function Navbar({ show }: { show: boolean }) {
  const history = useHistory();
  const t = useLocale();
  const { userInfo, userLoading } = useSelector((state: GlobalState) => state);
  const dispatch = useDispatch();
  const userInfoRef = useRef(userInfo);

  const [, setUserStatus] = useStorage('userStatus');
  const [role] = useStorage('userRole', 'admin');

  const { theme, setTheme } = useContext(GlobalContext);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [taskDrawerVisible, setTaskDrawerVisible] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [currentTaskProjectId, setCurrentTaskProjectId] = useState<string | null>(null);

  const { permissions: batchPermissions } = useBatchProjectPermissions({
    projectIds: currentTaskProjectId ? [currentTaskProjectId] : [],
    permissionCodes: PERMISSION_CODES,
    enabled: taskDrawerVisible && Boolean(currentTaskProjectId),
  });

  const drawerProjectPermissions = useMemo(() => {
    if (!currentTaskProjectId) {
      return {};
    }

    const rawPermissions = batchPermissions[currentTaskProjectId] || {};
    const mappedPermissions: Partial<ProjectPermissions> = {};

    Object.entries(PERMISSION_CODE_MAP).forEach(([code, propName]) => {
      mappedPermissions[propName as keyof ProjectPermissions] = rawPermissions[code] || false;
    });

    return {
      [currentTaskProjectId]: mappedPermissions as ProjectPermissions,
    };
  }, [batchPermissions, currentTaskProjectId]);

  async function logout() {
    // 使用 Supabase 登出
    await signOut();
    setUserStatus('logout');
    window.location.href = '/login';
  }

  function onMenuItemClick(key: string) {
    if (key === 'logout') {
      logout();
    } else {
      Message.info(`You clicked ${key}`);
    }
  }

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  useEffect(() => {
    dispatch({
      type: 'update-userInfo',
      payload: {
        userInfo: {
          ...userInfoRef.current,
          permissions: generatePermission(role),
        },
      },
    });
  }, [dispatch, role]);

  const handleOpenSearchModal = useCallback(() => {
    setSearchModalVisible(true);
  }, []);

  const handleCloseSearchModal = useCallback(() => {
    setSearchModalVisible(false);
  }, []);

  const handleTaskResultClick = useCallback((task: Task) => {
    setSearchModalVisible(false);
    setCurrentTaskId(task.id);
    setCurrentTaskProjectId(task.projectId || null);
    setTaskDrawerVisible(true);
  }, []);

  const handleProjectResultClick = useCallback((project: Project) => {
    setSearchModalVisible(false);
    history.push(`/tasks/${project.id}`);
  }, [history]);

  const handleTaskDrawerClose = useCallback(() => {
    setTaskDrawerVisible(false);
    setCurrentTaskId(null);
    setCurrentTaskProjectId(null);
  }, []);

  if (!show) {
    return (
      <div className={styles['fixed-settings']}>
        {/* 页面配置入口已移除 */}
      </div>
    );
  }

  const droplist = (
    <Menu onClickMenuItem={onMenuItemClick} style={{ minWidth: 200 }}>
      {/* 用户信息区域 */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
          {userInfo?.name || '未知用户'}
        </div>
        <div style={{ fontSize: 12, color: '#86909c', marginBottom: 8 }}>
          {userInfo?.department || '暂无部门'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {userInfo?.roles && userInfo.roles.length > 0 ? (
            userInfo.roles.map((role) => (
              <span
                key={role.id}
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  fontSize: 12,
                  backgroundColor: '#f2f3f5',
                  color: '#4e5969',
                  borderRadius: 2,
                }}
              >
                {role.name}
              </span>
            ))
          ) : (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                fontSize: 12,
                backgroundColor: '#f2f3f5',
                color: '#4e5969',
                borderRadius: 2,
              }}
            >
              暂无角色
            </span>
          )}
        </div>
      </div>
      <Divider style={{ margin: '4px 0' }} />
      <Menu.Item key="logout">
        <IconPoweroff className={styles['dropdown-icon']} />
        {t['navbar.logout']}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles.navbar}>
      <div className={styles.left}>
        <div className={styles.logo}>
          <Logo />
          <div className={styles['logo-name']}>SimpleUX Pro</div>
        </div>
      </div>
      <ul className={styles.right}>
        <li>
          <Input.Search
            className={styles.round}
            placeholder="搜索项目 / 任务"
            readOnly
            onClick={handleOpenSearchModal}
            onFocus={handleOpenSearchModal}
          />
        </li>
        <li>
          <MessageBox>
            <IconButton icon={<IconNotification />} />
          </MessageBox>
        </li>
        <li>
          <Tooltip
            content={
              theme === 'light'
                ? t['settings.navbar.theme.toDark']
                : t['settings.navbar.theme.toLight']
            }
          >
            <IconButton
              icon={theme !== 'dark' ? <IconMoonFill /> : <IconSunFill />}
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            />
          </Tooltip>
        </li>
        {/* 页面配置入口已移除 */}
        {userInfo && (
          <li>
            <Dropdown droplist={droplist} position="br" disabled={userLoading}>
              <UserAvatar
                size={32}
                name={userInfo.name || '未知用户'}
                avatarUrl={userLoading ? null : (userInfo.avatar || null)}
                style={{ cursor: 'pointer' }}
              />
            </Dropdown>
          </li>
        )}
      </ul>

      <GlobalSearchModal
        visible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onTaskClick={handleTaskResultClick}
        onProjectClick={handleProjectResultClick}
      />

      <TaskDetailDrawer
        visible={taskDrawerVisible}
        taskId={currentTaskId}
        onClose={handleTaskDrawerClose}
        onSuccess={handleTaskDrawerClose}
        projectPermissions={drawerProjectPermissions}
        currentUserId={userInfo?.id}
      />
    </div>
  );
}

export default Navbar;
